import { useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";

// =============================================================================
// Types
// =============================================================================

interface NavLink {
  label: string;
  href: string;
}

export type SitePage = "home" | "features" | "pricing" | "about" | "docs" | "marketplace" | "console";

interface NavBarProps {
  currentPage?: SitePage;
  onNavigate?: (page: SitePage) => void;
}

// =============================================================================
// Constants
// =============================================================================

const NAV_LINKS: { label: string; page: SitePage }[] = [
  { label: "Features", page: "features" },
  { label: "Pricing", page: "pricing" },
  { label: "Marketplace", page: "marketplace" },
  { label: "About", page: "about" },
  { label: "Docs", page: "docs" },
  { label: "Console", page: "console" },
];

// =============================================================================
// HVME Logo Component
// =============================================================================

function HVMELogo({ onNavigate }: { onNavigate: (page: SitePage) => void }) {
  return (
    <button
      onClick={() => onNavigate("home")}
      className="flex items-center group transition-transform duration-300 hover:scale-105"
    >
      <img
        src="/logo.png"
        alt="HVME"
        className="h-10 w-auto"
      />
    </button>
  );
}

// =============================================================================
// Desktop Navigation Links
// =============================================================================

function DesktopNavLinks({
  activePage,
  onNavigate
}: {
  activePage: SitePage;
  onNavigate: (page: SitePage) => void;
}) {
  return (
    <nav className="hidden md:flex items-center gap-1">
      {NAV_LINKS.map(({ label, page }) => {
        const isActive = activePage === page;

        return (
          <button
            key={page}
            onClick={() => onNavigate(page)}
            className="relative px-4 py-2 font-nav text-sm font-bold uppercase tracking-widest text-white/90 transition-colors duration-300 hover:text-white group"
          >
            {label}
            {/* Hover underline animation */}
            <span
              className={`
                absolute bottom-0 left-4 right-4 h-[2px] bg-hvme-primary
                transform origin-left transition-transform duration-300
                ${isActive ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"}
              `}
            />
            {/* Active indicator with animation */}
            {isActive && (
              <motion.span
                layoutId="active-nav-indicator"
                className="absolute bottom-0 left-4 right-4 h-[2px] bg-gradient-to-r from-hvme-plasma-violet to-hvme-aurora-lilac"
                transition={{ type: "spring", stiffness: 400, damping: 30 }}
              />
            )}
          </button>
        );
      })}
    </nav>
  );
}

// =============================================================================
// Launch Console Button
// =============================================================================

function LaunchConsoleButton({
  className = "",
  onNavigate,
}: {
  className?: string;
  onNavigate: (page: SitePage) => void;
}) {
  return (
    <motion.button
      onClick={() => onNavigate("console")}
      className={`
        inline-flex items-center justify-center
        px-6 py-2.5 rounded-full
        bg-hvme-primary text-white
        font-nav text-sm font-bold uppercase tracking-wider
        transition-all duration-300
        hover:bg-hvme-primary/90
        ${className}
      `}
      whileHover={{
        scale: 1.03,
        boxShadow: "0 0 30px rgba(220,160,250,0.5), 0 0 60px rgba(220,160,250,0.2)",
      }}
      whileTap={{ scale: 0.97 }}
    >
      <motion.span
        className="relative"
        animate={{
          textShadow: [
            "0 0 0px transparent",
            "0 0 8px rgba(255,255,255,0.3)",
            "0 0 0px transparent",
          ],
        }}
        transition={{ duration: 2, repeat: Infinity }}
      >
        Launch Console
      </motion.span>
    </motion.button>
  );
}

// =============================================================================
// Hamburger Menu Icon
// =============================================================================

function HamburgerIcon({ isOpen, onClick }: { isOpen: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="md:hidden relative w-10 h-10 flex items-center justify-center text-white focus:outline-none"
      aria-label={isOpen ? "Close menu" : "Open menu"}
    >
      <div className="relative w-6 h-5 flex flex-col justify-between">
        <span
          className={`
            block h-0.5 w-6 bg-white rounded-full transform transition-all duration-300 origin-center
            ${isOpen ? "rotate-45 translate-y-[9px]" : ""}
          `}
        />
        <span
          className={`
            block h-0.5 w-6 bg-white rounded-full transition-all duration-300
            ${isOpen ? "opacity-0 scale-x-0" : "opacity-100 scale-x-100"}
          `}
        />
        <span
          className={`
            block h-0.5 w-6 bg-white rounded-full transform transition-all duration-300 origin-center
            ${isOpen ? "-rotate-45 -translate-y-[9px]" : ""}
          `}
        />
      </div>
    </button>
  );
}

// =============================================================================
// Mobile Drawer
// =============================================================================

function MobileDrawer({
  isOpen,
  onClose,
  activePage,
  onNavigate,
}: {
  isOpen: boolean;
  onClose: () => void;
  activePage: SitePage;
  onNavigate: (page: SitePage) => void;
}) {
  // Prevent body scroll when drawer is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const handleNavClick = (page: SitePage) => {
    onNavigate(page);
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm md:hidden"
            onClick={onClose}
          />

          {/* Drawer */}
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            className="fixed top-0 right-0 z-50 h-full w-[280px] bg-hvme-bg-deep/95 backdrop-blur-xl border-l border-white/5 md:hidden"
          >
            {/* Drawer header */}
            <div className="flex items-center justify-between h-16 px-6 border-b border-white/5">
              <span className="font-nav text-sm font-bold uppercase tracking-widest text-white/70">
                Menu
              </span>
              <button
                onClick={onClose}
                className="w-8 h-8 flex items-center justify-center text-white/70 hover:text-white transition-colors"
                aria-label="Close menu"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Nav links */}
            <nav className="flex flex-col py-6">
              {NAV_LINKS.map(({ label, page }, index) => {
                const isActive = activePage === page;

                return (
                  <motion.button
                    key={page}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 + 0.1 }}
                    onClick={() => handleNavClick(page)}
                    className={`
                      relative px-6 py-4 font-nav text-sm font-bold uppercase tracking-widest text-left
                      transition-colors duration-300
                      ${isActive
                        ? "text-hvme-primary bg-hvme-primary/10"
                        : "text-white/80 hover:text-white hover:bg-white/5"
                      }
                    `}
                  >
                    {label}
                    {isActive && (
                      <span className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-gradient-to-b from-hvme-plasma-violet to-hvme-aurora-lilac rounded-r" />
                    )}
                  </motion.button>
                );
              })}
            </nav>

            {/* CTA button in drawer */}
            <div className="px-6 pt-4">
              <LaunchConsoleButton className="w-full" onNavigate={onNavigate} />
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

// =============================================================================
// Main NavBar Component
// =============================================================================

export function NavBar({ currentPage = "home", onNavigate }: NavBarProps) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleNavigate = (page: SitePage) => {
    if (onNavigate) {
      onNavigate(page);
    }
  };

  return (
    <>
      <header
        className="
          sticky top-0 z-50
          h-16 md:h-[72px]
          bg-[rgba(20,20,60,0.8)] backdrop-blur-[20px]
          border-b border-white/5
        "
      >
        <div className="mx-auto max-w-[1400px] h-full flex items-center justify-between px-4 md:px-6">
          {/* Left: Logo */}
          <HVMELogo onNavigate={handleNavigate} />

          {/* Center: Desktop Nav Links */}
          <DesktopNavLinks activePage={currentPage} onNavigate={handleNavigate} />

          {/* Right: CTA + Mobile Menu Toggle */}
          <div className="flex items-center gap-4">
            {/* Desktop CTA */}
            <LaunchConsoleButton className="hidden md:inline-flex" onNavigate={handleNavigate} />

            {/* Mobile hamburger */}
            <HamburgerIcon
              isOpen={isMobileMenuOpen}
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            />
          </div>
        </div>
      </header>

      {/* Mobile drawer */}
      <MobileDrawer
        isOpen={isMobileMenuOpen}
        onClose={() => setIsMobileMenuOpen(false)}
        activePage={currentPage}
        onNavigate={handleNavigate}
      />
    </>
  );
}

export default NavBar;
